import { useFlags } from '@atlaskit/flag';
import { AlertsApiService, VehicleChecksApiService, VehiclesApiService } from 'core/api';
import { useDialog } from 'core/providers/DialogProvider';
import dayjs from 'dayjs';
import { where } from 'firebase/firestore';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SharedButton from 'shared/components/buttons/button';
import SharedTabButton from 'shared/components/buttons/tab-button/tab-button';
import PageHeader from 'shared/components/page-header/page-header';
import PageHeaderSkeleton from 'shared/components/page-header/page-header-skeleton';
import ConfirmationDialog from 'modules/dialogs/confirmation-dialog';
import { IVehicleDao } from 'core/api/types/vehicles.interface';
import { VehicleTab, vehicleTabs } from 'core/constants/vehicle-tab';
import AddEditVehicleDialog from './dialogs/add-edit-vehicle-dialog';
import { getFuelTypeNameFromKey } from 'core/constants/fuel-types';
import { useDocument } from 'core/hooks/useDocument';
import { useCollection } from 'core/hooks/useCollection';
import { IVehicleCheckDao } from 'core/api/types/vehicle-checks.interface';
import SharedTable from 'shared/components/table/table';
import { ISharedTableColumn, ISharedTableCustomCellTemplate } from 'shared/components/table/table-interface';
import { deleteItem } from 'core/utilities/api-wrapper';
import { alertTemplates } from './templates';
import { IAlertDao } from 'core/api/types/alerts.interface';
import AcknowledgeAlertDialog from 'modules/alerts/dialogs/acknowledge-alert-dialog';

const VehicleChecksTableColumns: ISharedTableColumn[] = [
  { label: 'Vehicle check', key: 'name', templateId: 'text', width: 15 },
  { label: 'Check Date', key: 'checkDate', templateId: 'checkDate', width: 10 },
  { label: 'Driver', key: 'driver', templateId: 'driver', width: 10 },
  { label: '', key: 'action', templateId: 'action', width: 10 },
];

const AlertsTableColumns: ISharedTableColumn[] = [
  { label: 'Alert reason', key: 'reason', templateId: 'text', width: 10 },
  { label: 'Triggered at', key: 'triggeredAt', templateId: 'triggeredAt', width: 10 },
  { label: 'Acknowledged by', key: 'acknowledgedBy', templateId: 'acknowledgedBy', width: 10 },
  { label: 'Acknowledged at', key: 'acknowledgedAt', templateId: 'acknowledgedAt', width: 10 },
  { label: 'Comment', key: 'comment', templateId: 'text', width: 10 },
  { label: '', key: 'action', templateId: 'action', width: 10 },
];

const VehiclesItem = () => {
  const { uid } = useParams();
  const flags = useFlags();
  const dialog = useDialog();
  const navigate = useNavigate();

  const [selectedTab, setSelectedTab] = useState<string>(VehicleTab.ALERTS);

  const vehicleConstraints = useMemo(() => [where('vehicleUid', '==', uid)], [uid]);
  const alertsConstraints = useMemo(() => [where('documentId', '==', uid)], [uid]);

  const { tableRows, loading: vehicleChecksLoading } = useCollection<IVehicleCheckDao>({
    apiService: VehicleChecksApiService,
    constraints: vehicleConstraints,
  });

  const { tableRows: alerts, loading: alertsLoading } = useCollection<IAlertDao>({
    apiService: AlertsApiService,
    constraints: alertsConstraints,
  });

  const { data: vehicle, loading: vehicleLoading } = useDocument<IVehicleDao>({
    apiService: VehiclesApiService,
    uid,
    navigateTo: '/vehicles',
  });

  const actionCellTemplate = (alert: IAlertDao) => (
    <>
      {alert.acknowledged ? null : (
        <div className='w-full flex justify-end space-x-2'>
          <SharedButton
            onClick={() => dialog?.openDialog(<AcknowledgeAlertDialog alert={alert} />)}
            type='button'
            appearance='subtle'
            spacing='compact'
            label='Acknowledge'
          />
        </div>
      )}
    </>
  );

  const vehicleChecksActionCellTemplate = (vehicleCheck: IVehicleCheckDao) => (
    <>
      <div className='w-full flex justify-end space-x-2'>
        <SharedButton
          onClick={() => navigate(`/vehicle-checks/${vehicleCheck.uid}`)}
          type='button'
          appearance='subtle'
          spacing='compact'
          label='View'
        />
      </div>
    </>
  );

  const checkDateCellTemplate = (vehicleCheck: IVehicleCheckDao) => {
    return dayjs(vehicleCheck.checkDate.toDate()).format('HH:mm, DD/MM/YYYY');
  };

  const driverCellTemplate = (vehicleCheck: IVehicleCheckDao) => {
    return vehicleCheck.driver.fullName;
  };

  const customTemplates: ISharedTableCustomCellTemplate[] = [
    {
      template: actionCellTemplate,
      id: 'action',
    },
  ];

  const vehicleChecksCustomTemplates: ISharedTableCustomCellTemplate[] = [
    {
      template: vehicleChecksActionCellTemplate,
      id: 'action',
    },
    {
      template: checkDateCellTemplate,
      id: 'checkDate',
    },
    {
      template: driverCellTemplate,
      id: 'driver',
    },
  ];

  const getTabContent = (vehicle: IVehicleDao) => {
    switch (selectedTab) {
      case VehicleTab.ALERTS:
        return (
          <>
            <SharedTable
              columns={AlertsTableColumns}
              rows={alerts}
              isLoading={alertsLoading}
              customTemplates={[...customTemplates, ...alertTemplates]}
            />
          </>
        );
      case VehicleTab.VEHICLE_CHECKS:
        return (
          <>
            <SharedTable
              columns={VehicleChecksTableColumns}
              rows={tableRows}
              isLoading={vehicleChecksLoading}
              customTemplates={vehicleChecksCustomTemplates}
            />
          </>
        );
    }
  };

  const VehiclesItemDetails = ({ vehicle }: { vehicle: IVehicleDao }) => {
    const VehicleDetails = [
      {
        label: 'Registration',
        value: vehicle.registration,
        key: 'registration',
      },
      {
        label: 'Make',
        value: vehicle.make,
        key: 'make',
      },
      {
        label: 'Model',
        value: vehicle.model,
        key: 'model',
      },
      {
        label: 'Fuel type',
        value: getFuelTypeNameFromKey(vehicle.fuelType),
        key: 'fuelType',
      },
      {
        label: 'Mileage',
        value: tableRows.reduce((max, check) => Math.max(max, check.data.mileage), 0),
        key: 'mileage',
      },
      {
        label: 'In Use',
        value: vehicle.currentDriver ? 'Yes with driver: ' + vehicle.currentDriver.name : 'No',
        key: 'inUse',
      },
      {
        label: 'Created at',
        value: vehicle.created.at ? dayjs(vehicle.created.at.toDate()).format('HH:mm, DD/MM/YYYY') : 'Unknown',
        key: 'createdAt',
      },
      {
        label: 'Created by',
        value: vehicle.created.by.fullName ?? 'Unknown',
        key: 'createdBy',
      },
    ];

    return (
      <div className='flex-grow-0 flex-shrink-0 basis-[360px] bg-white shadow-md rounded-md'>
        <div className='border-b p-4 flex justify-between items-center'>
          <p className='headline-06'>Details</p>
          <div className='flex space-x-2'>
            <SharedButton
              onClick={() => dialog?.openDialog(<AddEditVehicleDialog vehicle={vehicle} />)}
              type='button'
              appearance='primary'
              label='Update'
            />
            <SharedButton
              onClick={() =>
                dialog?.openDialog(
                  <ConfirmationDialog
                    title='Delete vehicle'
                    textContent='Are you sure you want to delete this vehicle? This action cannot be undone.'
                    action={() => deleteItem(VehiclesApiService, vehicle, flags, dialog)}
                    buttonLabel='Delete Vehicle'
                  />
                )
              }
              type='button'
              appearance='danger'
              label='Delete'
            />
          </div>
        </div>
        <div className='p-4'>
          {VehicleDetails.map((section) => (
            <div key={section.key} className='mt-4 first:mt-0'>
              <p className='body-02 text-gray-500'>{section.label}</p>
              <p className='body-01 break-words'>{section.value}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <>
        {!vehicle || vehicleLoading ? (
          <PageHeaderSkeleton actions={[]} subtitle={true} />
        ) : (
          <>
            <PageHeader title={`${vehicle.registration}`} showBack={true} />
            <div className='flex flex-row py-6'>
              <VehiclesItemDetails vehicle={vehicle} />
              <div className='pl-6 w-full'>
                <div className='relative'>
                  <div className='flex relative z-10'>
                    {vehicleTabs.map((tab) => (
                      <SharedTabButton key={tab.key} changeTab={setSelectedTab} tab={tab} currentTab={selectedTab} />
                    ))}
                  </div>
                  <div className='absolute bottom-0 left-0 right-0 h-px bg-gray-200' />
                </div>
                <div className='mt-6'>{getTabContent(vehicle)}</div>
              </div>
            </div>
          </>
        )}
      </>
    </>
  );
};

export default VehiclesItem;
